<ph-chat-gutter>

    <!-- The whole empty state, once. It used to be a splash header naming the
         panel stacked on an "All caught up" card that said the same thing in
         other words; the two are one block now. -->
    <ph-empty-state *ngIf="panelEmpty" icon="check-circle" heading="All caught up" center>
        Nothing needs you right now. Everything Gable proposes or does lands here — waiting on
        your approval, or already committed.
    </ph-empty-state>

    <!-- ======================= Success banner ======================= -->
    <ph-alert *ngIf="successBanner as banner" [status]="banner.variant === 'error' ? 'error' : 'success'" dismissible (ph-dismiss)="dismissBanner()">
        <ph-toolbar justify="between">
            <span>{{ banner.message }}</span>
            <ph-link-button *ngIf="banner.variant === 'success'" trailing-icon="arrow-right" (click)="onBannerView()">View</ph-link-button>
        </ph-toolbar>
    </ph-alert>

    <!-- ======================= Tasks (option B: long deliverables) ======================= -->
    <ph-stack *ngIf="tasks.length > 0">
        <ph-page-header dense size="small" [level]="2">Tasks</ph-page-header>
        <ng-container *ngFor="let task of tasks; trackBy: trackByTaskKey">
            <ph-toolbar nowrap *ngIf="task.state === 'running'">
                <app-gable-deliverable-card
                    [loading]="true"
                    [loadingLabel]="task.title"
                    [loadingHint]="runningHint(task)">
                </app-gable-deliverable-card>
                <ph-compact-button *ngIf="isWorkflowRunTask(task)" variant="ghost" size="medium" icon="x" aria-label="Dismiss" (click)="dismissTask(task)"></ph-compact-button>
            </ph-toolbar>

            <ng-container *ngIf="task.state === 'ready'">
                <app-gable-ledger-intelligence-card
                    *ngIf="ledgerPayload(task) as ledger; else deliverableReady"
                    [payload]="ledger">
                </app-gable-ledger-intelligence-card>
                <ng-template #deliverableReady>
                    <app-gable-deliverable-card [payload]="deliverablePayload(task)"></app-gable-deliverable-card>
                </ng-template>
            </ng-container>

            <ph-alert *ngIf="task.state === 'failed'" status="error" variant="lighter" size="small" dismissible (ph-dismiss)="dismissTask(task)">
                {{ task.error || 'This analysis could not be completed.' }}
            </ph-alert>
        </ng-container>
    </ph-stack>

    <!-- ======================= Needs your review ======================= -->
    <ng-container *ngIf="!queueEmpty">
        <!-- No spacer: the count belongs to the heading, so it sits beside it rather
             than pinned to the far edge of a 720px panel where the two read as
             unrelated. The Tasks header above keeps both. -->
        <ph-page-header dense size="small" [level]="2">
            Needs your review
            <ph-badge slot="title-actions" *ngIf="waitingCount > 0" variant="filled" color="blue" size="small">{{ waitingCount }} waiting</ph-badge>
            <ph-link-button slot="title-actions" *ngIf="pmcId !== null" trailing-icon="arrow-right" [routerLink]="['/pmc', pmcId, 'gable-dashboard']" (click)="onSeeAllInTable()">Open Gable dashboard</ph-link-button>
        </ph-page-header>

        <!-- Filter chips — directly under the heading, filtering queue + feed -->
            <ph-toolbar>
                <ph-chip *ngFor="let filter of visibleFilters; trackBy: trackByFilterKey"
                         size="small"
                         [counter]="filter.count"
                         [selected]="activeFilter === filter.key"
                         [attr.aria-pressed]="activeFilter === filter.key"
                         (click)="setFilter(filter.key)">
                    {{ filter.label }}
                </ph-chip>
            </ph-toolbar>
    </ng-container>

    <ng-container *ngIf="!queueEmpty; else queueEmptyTpl">
      <ph-stack gap="loose">
        <ph-stack *ngFor="let group of reviewGroups; trackBy: trackByGroupModule" gap="tight">

            <ph-toolbar nowrap gap="tight">
                <ph-label [icon]="moduleIconName(group.module)">{{ group.label }}</ph-label>
                <ph-badge variant="lighter" color="gray" size="small">{{ group.cards.length }}</ph-badge>
            </ph-toolbar>

            <ph-stack gap="snug">
              <!-- ONE card per underlying record: a request with one suggestion and a
                   request with three look structurally identical. The suggestions are
                   arranged INSIDE the card by shape (single / composite / choose). -->
              <ng-container *ngFor="let card of group.cards; trackBy: trackByCardKey">

                <!-- Acted-on card: green result bar naming what happened -->
                <ph-alert *ngIf="card.doneLabel; else liveCard" status="success" variant="lighter" size="small">{{ card.doneLabel }}</ph-alert>

                <ng-template #liveCard>

                    <!-- Legacy CRUD proposals: summary row + approve/reject -->
                    <ph-list-row *ngIf="card.shape === 'single' && card.primary.cardPayload === null; else typedCard"
                                 variant="card" plain
                                 [aside]="card.age"
                                 [heading]="card.primary.proposal.summary"
                                 [meta]="'Expires ' + (card.primary.proposal.expiresAt | date:'shortTime')">
                        <ph-badge slot="actions" variant="light" color="gray" *ngIf="card.primary.proposal.organizationName as orgName">{{ orgName }}</ph-badge>
                        <ph-actions slot="detail" align="start" *ngIf="!card.primary.resolvedLabel">
                            <ph-button size="small" icon="check"
                                       [disabled]="card.primary.busy"
                                       (click)="approveCrud(card.primary)">Approve</ph-button>
                            <ph-button variant="secondary" mode="filled" size="small"
                                       [disabled]="card.primary.busy"
                                       (click)="rejectCrud(card.primary)">Reject</ph-button>
                        </ph-actions>
                        <ph-hint slot="detail" *ngIf="card.primary.resolvedLabel">{{ card.primary.resolvedLabel }}, done</ph-hint>
                    </ph-list-row>

                    <ng-template #typedCard>
                        <!-- Collapsed row: record identity · what Gable proposes · who and where -->
                        <ph-list-row variant="card"
                                     wrap
                                     [heading]="card.title"
                                     [meta]="card.meta"
                                     [note]="card.note"
                                     [aside]="card.age"
                                     [open]="isCardExpanded(card)" expandable
                                     (ph-select)="toggleCard(card)">
                            <ph-badge slot="actions" variant="light" color="gray" *ngIf="card.primary.proposal.organizationName as orgName">{{ orgName }}</ph-badge>
                            <ph-badge slot="actions" variant="light" color="gray" *ngIf="card.shape === 'single' && entryAmount(card.primary) as amt">{{ amt }}</ph-badge>
                        <ph-stack slot="detail" *ngIf="isCardExpanded(card)">

                            <!-- Single suggestion: the full action-preview card owns the body -->
                            <ng-container *ngIf="card.shape === 'single'; else multiBody">
                                <ph-link-button *ngIf="cardRoute(card) as route" icon="arrow-square-out" [routerLink]="route" [queryParams]="cardRouteQuery(card)">
                                    {{ cardLinkLabel(card) }}
                                </ph-link-button>
                                <app-gable-action-preview-card [payload]="card.primary.cardPayload"
                                                               [pmcId]="pmcId"
                                                               [hideDeepLink]="cardRoute(card) !== null"
                                                               [hideHeaderSection]="true"
                                                               [viewerCapability]="card.primary.proposal.viewerCapability"
                                                               (confirm)="onCardConfirm(card.primary, $event)"
                                                               (cancel)="onCardCancel(card.primary, $event)"
                                                               (snooze)="onCardSnooze(card.primary, $event)"
                                                               (utilityAction)="onCardUtility(card.primary, $event)">
                                </app-gable-action-preview-card>
                            </ng-container>

                            <!-- Composite / choose: the card arranges the suggestions itself -->
                            <ng-template #multiBody>
                                <ph-link-button *ngIf="cardRoute(card) as route" icon="arrow-square-out" [routerLink]="route" [queryParams]="cardRouteQuery(card)">
                                    {{ cardLinkLabel(card) }}
                                </ph-link-button>

                                <ph-alert *ngIf="cardReasoning(card) as why" status="feature" variant="lighter" size="small">{{ why }}</ph-alert>

                                <ng-container *ngIf="card.shape === 'composite'">
                                    <ph-stack gap="tight">
                                        <ph-label>The action</ph-label>
                                        <ph-list-row variant="card" plain [heading]="entryTitle(card.primary)" [meta]="mergeConsequence(card) || ''">
                                            <ph-rich-text slot="detail" *ngIf="entryDraft(card.primary) as draft" size="sm" [markdown]="draft"></ph-rich-text>
                                        </ph-list-row>
                                    </ph-stack>

                                    <ph-stack gap="tight" *ngIf="card.followUps.length">
                                        <ph-label>Also do</ph-label>
                                        <ph-stack gap="snug">
                                            <ph-list-row *ngFor="let fu of card.followUps; trackBy: trackByFollowUp"
                                                         variant="card"
                                                         [heading]="entryTitle(fu.entry)"
                                                         [meta]="entryConsequence(fu.entry)"
                                                         [muted]="!fu.checked"
                                                         (ph-select)="toggleFollowUp(card, fu)">
                                                <ph-checkbox slot="actions" [checked]="fu.checked" [attr.aria-label]="entryTitle(fu.entry)" (ph-change)="toggleFollowUp(card, fu)"></ph-checkbox>
                                                <ph-rich-text slot="detail" *ngIf="entryDraft(fu.entry) as draft" size="sm" [markdown]="draft"></ph-rich-text>
                                            </ph-list-row>
                                        </ph-stack>
                                    </ph-stack>
                                </ng-container>

                                <ph-radio-group *ngIf="card.shape === 'choose'"
                                                aria-label="Choose how to handle this"
                                                [items]="optionItems(card)"
                                                [value]="card.selectedOptionId"
                                                (ph-change)="selectOptionById(card, $event)"></ph-radio-group>

                                <ph-alert *ngIf="cardBlocked(card) as blocked" status="warning" variant="lighter" size="small">
                                    {{ blocked.reason }}<ng-container *ngIf="blocked.whoCan.length"> {{ blocked.whoCan.join(', ') }} can approve.</ng-container>
                                </ph-alert>

                                <ph-actions align="between">
                                    <ph-actions align="start">
                                        <ph-button size="small" icon="check"
                                                [disabled]="card.committing || cardBlocked(card) !== null || (card.shape === 'choose' && card.selectedOptionId === null)"
                                                (click)="commitCard(card)">
                                            {{ card.committing ? 'Working…' : cardPrimaryLabel(card) }}
                                        </ph-button>
                                        <ph-button variant="secondary" mode="filled" size="small"
                                                [disabled]="card.committing"
                                                (click)="skipCard(card)">Skip</ph-button>
                                    </ph-actions>
                                    <ph-button variant="secondary" mode="ghost" size="small"
                                            [disabled]="card.committing"
                                            (click)="snoozeCard(card)">Snooze</ph-button>
                                </ph-actions>
                            </ng-template>
                        </ph-stack>
                        </ph-list-row>
                    </ng-template>
                </ng-template>
              </ng-container>
            </ph-stack>
        </ph-stack>
      </ph-stack>
    </ng-container>

    <!-- Only when there is a feed underneath — with nothing at all on the page
         the merged empty block above owns the message. `feedLoaded` gates it so
         the card cannot render against a still-loading feed and then swap for
         the empty block a beat later. -->
    <ng-template #queueEmptyTpl>
        <ph-empty-state *ngIf="feedLoaded && !panelEmpty" icon="check-circle" heading="All caught up" center>
            Nothing needs you right now. New suggestions will appear here.
        </ph-empty-state>
    </ng-template>

    <!-- Feed load failure — distinct from the empty state, with a retry. -->
    <ph-empty-state *ngIf="feedError" icon="warning" heading="Couldn't load activity" center>
        Something went wrong fetching the feed.
        <ph-button slot="actions" variant="secondary" mode="filled" size="small" (click)="retryFeed()">Try again</ph-button>
    </ph-empty-state>

    <!-- First load only. `feedLoaded` is the one-shot flag, NOT `feedLoading`:
         that one goes true again for every "Load more" page, and hanging a
         skeleton on it would replace the feed the user is already reading.
         `feedError` owns the failure case below, so it stands down here.

         Sits immediately above the feed it stands in for. At the top of the
         panel it was a placeholder for content that arrives last: during first
         load `queueLoaded` is false, so `queueEmpty` is false and the "Needs
         your review" block paints between the two — the feed then landed well
         below its own outline and the page jumped. -->
    <ph-skeleton *ngIf="!feedLoaded && !feedError" shape="list" [rows]="skeletonCards2.length" label="Loading activity"></ph-skeleton>

    <!-- ======================= Module-grouped feed ======================= -->
    <!-- No empty state on purpose: decided actions only earn screen space once they exist. -->
    <ng-container *ngIf="feedDays.length">
        <!-- The counterpart to "Needs your review". Without it the feed's first
             MODULE label was the only heading down here, so a group name read as
             the section title. The rule above splits the panel's two halves:
             what still wants a decision, and what has left the queue.
             Deliberately NOT "Already handled": this feed also carries `snoozed`
             (which comes back to the queue) and `expired` (never actioned), so a
             done-claim here would contradict the pill on the row underneath. -->
        <ph-divider></ph-divider>
        <ph-page-header dense size="small" [level]="3">Recent activity</ph-page-header>

      <ph-stack gap="loose">
        <ph-stack *ngFor="let day of feedDays; trackBy: trackByDayLabel" gap="tight">
            <ph-toolbar nowrap gap="tight">
                <ph-label [icon]="moduleIconName(day.module)">{{ day.label }}</ph-label>
                <ph-badge variant="lighter" color="gray" size="small">{{ day.items.length }}</ph-badge>
            </ph-toolbar>
            <div>
                <ph-list-row *ngFor="let item of day.items; trackBy: trackByItemId"
                             plain size="small"
                             [muted]="feedItemLapsed(item)"
                             [heading]="item.summary"
                             [meta]="dismissReasonLabel(item) ? 'Reason: ' + dismissReasonLabel(item) : ''"
                             [aside]="decidedTime(item)">
                    <ph-badge slot="actions" variant="light" color="gray" *ngIf="item.organizationName as orgName">{{ orgName }}</ph-badge>
                    <ph-badge slot="actions" size="small" [variant]="feedItemLapsed(item) ? 'stroke' : 'light'" [color]="statusBadgeColor(item)">{{ statusLabel(item) }}</ph-badge>
                </ph-list-row>
            </div>
        </ph-stack>
      </ph-stack>

        <ph-actions align="center" *ngIf="canLoadMore">
            <ph-button variant="secondary" mode="filled" size="small"
                    [disabled]="feedLoading"
                    (click)="loadMore()">
                {{ feedLoading ? 'Loading…' : 'Load more' }}
            </ph-button>
        </ph-actions>
    </ng-container>

</ph-chat-gutter>

<app-gable-dismiss-reason-dialog [open]="dismissDialogOpen" (closed)="onDismissDialogClosed($event)"></app-gable-dismiss-reason-dialog>
